import { useState, useEffect, useRef, FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Alert, Title, Body, Icon, Loader } from '../components/kern';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const oidcAutoRedirected = useRef(false);

  const { login, loginWithKeycloak, authMode, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  // If already authenticated, redirect immediately
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Auto-redirect to Keycloak in OIDC mode
  useEffect(() => {
    if (authMode === 'oidc' && !isAuthenticated && !oidcAutoRedirected.current) {
      oidcAutoRedirected.current = true;
      setIsLoading(true);
      loginWithKeycloak().catch((err) => {
        setError(err instanceof Error ? err.message : 'SSO-Anmeldung fehlgeschlagen');
        setIsLoading(false);
      });
    }
  }, [authMode, isAuthenticated, loginWithKeycloak]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeycloakLogin = async () => {
    setError('');
    setIsLoading(true);
    try {
      await loginWithKeycloak();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'SSO-Anmeldung fehlgeschlagen');
      setIsLoading(false);
    }
  };

  // Show loader while auth config is being fetched
  if (authMode === null) {
    return (
      <div className="login-page">
        <div className="loading-container">
          <Loader />
        </div>
      </div>
    );
  }

  // In OIDC mode: show loader during auto-redirect, or error with retry button
  if (authMode === 'oidc') {
    return (
      <div className="login-page">
        <article className="kern-card login-card">
          <div className="kern-card__container">
            <header className="kern-card__header login-header">
              <div className="login-logo">
                <Icon name="checklist" size="x-large" />
              </div>
              <Title size="large" as="h1">
                KERN Feedback
              </Title>
              <Body muted>
                {error ? 'SSO-Anmeldung fehlgeschlagen' : 'Weiterleitung zur SSO-Anmeldung...'}
              </Body>
            </header>

            <section className="kern-card__body">
              {error ? (
                <>
                  <Alert variant="danger" title="Verbindungsfehler">
                    {error}
                  </Alert>
                  <div style={{ marginTop: '16px' }}>
                    <Button
                      variant="primary"
                      block
                      onClick={handleKeycloakLogin}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Weiterleitung...' : 'Erneut versuchen'}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="loading-container">
                  <Loader />
                </div>
              )}
            </section>

            <footer className="kern-card__footer" />
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="login-page">
      <article className="kern-card login-card">
        <div className="kern-card__container">
          <header className="kern-card__header login-header">
            <div className="login-logo">
              <Icon name="checklist" size="x-large" />
            </div>
            <Title size="large" as="h1">
              KERN Feedback
            </Title>
            <Body muted>Melde dich an, um fortzufahren</Body>
          </header>

          <section className="kern-card__body">
            {error && (
              <Alert variant="danger" title="Fehler">
                {error}
              </Alert>
            )}

            <form className="login-form" onSubmit={handleSubmit}>
              <Input
                label="E-Mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                required
                autoComplete="email"
                autoFocus
              />

              <Input
                label="Passwort"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />

              <Button type="submit" variant="primary" block disabled={isLoading}>
                {isLoading ? 'Anmelden...' : 'Anmelden'}
              </Button>
            </form>
          </section>

          <footer className="kern-card__footer" />
        </div>
      </article>
    </div>
  );
}
